/**
 * @fileOverview The main entry point to your standalone. Start editing here.
 */

import React from 'react'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/site.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/header.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/image.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/list.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/grid.css'
import './styles.scss'
import { Masthead } from '@ta-interaktiv/react-masthead'
import { Footer } from '@ta-interaktiv/react-footer'
import { ThemeSwitch } from '@ta-interaktiv/react-theme-switch'
import { FeedbackMessage } from '@ta-interaktiv/react-feedback-message'
import { Waypoint } from 'react-waypoint'
import Glacier from '../../components/glacier'
import VanishedGlacier from '../../components/vanished-glacier'
import { AutofitIFrame } from '@ta-interaktiv/react-autofit-iframe'

const ARTICLE_ID = '24143545'
const ADDITIONAL_SHARES = 2974

export function AppComponent() {
  return (
    <div className='index'>
      <Masthead articleId={ARTICLE_ID} additionalShares={ADDITIONAL_SHARES} />
      <ThemeSwitch />

      <header className='ui vertical  very fitted  segment'>
        <div className='ui container'>
          <div className='ui header'>
            <h1>So schmolzen die Schweizer Gletscher in 160 Jahren weg</h1>
            <h2>
              Auch wenn der Mensch schon morgen keine Treibhausgase mehr
              produzieren würde, die meisten Gletscher sind verloren. Wir haben
              das Abschmelzen visualisiert.
            </h2>
          </div>
        </div>
      </header>

      <main>
        <div className='ui vertical segment'>
          <div className='ui text container'>
            <div className='ta metainfo'>
              <div className='ta author'>
                Mathias Lutz und Marc Brupbacher, Interaktiv-Team
              </div>
              <div className='ta updated'>Aktualisiert am 2. August 2017</div>
            </div>
            <div className='ta text'>
              <p>
                Die Schweizer Gletscher erreichten ihre grösste Ausdehnung
                während der kleinen Eiszeit um das Jahr 1850. Die gesamte
                Gletscherfläche betrug damals 1735 km&sup2; – exakt die Grösse
                des Kantons Zürich. Heute sind es nur noch 890 km&sup2;.{' '}
                <strong>
                  Innerhalb von 166 Jahren ist die Hälfte der Gletscherfläche
                  der Schweiz geschmolzen.
                </strong>
              </p>
              <img
                src={require('./img/vergleich_zh.gif')}
                alt=''
                className='ui fluid image'
                style={{ marginTop: '1em' }}
              />
            </div>
            <div className='ta text'>
              <p>
                Auch die Eisdicke ging dramatisch zurück. Der Glaziologe
                Matthias Huss von der ETH Zürich/Universität Freiburg schätzt
                das Eisvolumen für 1850 auf rund 130 km&sup3;. 2016 waren es
                noch 54 km&sup3;, also fast 60 Prozent weniger. «Die Schweizer
                Gletscher kann man nicht mehr retten», sagt Huss. Selbst mit den
                grössten Anstrengungen zur CO2-Reduktion würden 80 bis 90
                Prozent der Eismassen bis ins Jahr 2100 verloren gehen. «Eine
                Verlangsamung der Erderwärmung kommt für die Schweizer Gletscher
                zu spät», so Huss. Auch Samuel Nussbaumer vom World Glacier
                Monitoring Service der Universität Zürich sieht keine Rettung:
                «Ich hoffe einzig darauf, dass wir wenigstens die
                höchstgelegensten Gletscher der Alpen zumindest in Bruchstücken
                erhalten können.»
              </p>
            </div>
          </div>
          <div className='ui container' style={{ marginTop: '2em' }}>
            <h3>Veränderung der 38 grössten Gletscher der Schweiz seit 1850</h3>
            <div className='ui grid all-glaciers'>
              <div className='doubling nine column row'>
                <div className='three wide centered column'>
                  <Glacier
                    n='Grosser Aletsch'
                    k='VS'
                    l='-19.66'
                    p='-18.61'
                    url='https://map.geo.admin.ch/?topic=ech&lang=en&bgLayer=ch.swisstopo.pixelkarte-farbe&layers=WMS%7C%7CSGI_1850%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1850%7C%7C1.3.0,WMS%7C%7CSGI_1973%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1973%7C%7C1.3.0,WMS%7C%7CSGI_2010%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_2010%7C%7C1.3.0&X=144379.75&Y=648306.63&zoom=5&layers_visibility=false,false,false'
                    classes='ui small black header'
                  />
                </div>
                <div className='three wide centered column'>
                  <Glacier
                    n='Gorner'
                    k='VS'
                    l='-12.72'
                    p='-19.38'
                    url='https://map.geo.admin.ch/?topic=ech&lang=en&bgLayer=ch.swisstopo.pixelkarte-farbe&layers=WMS%7C%7CSGI_1850%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1850%7C%7C1.3.0,WMS%7C%7CSGI_1973%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1973%7C%7C1.3.0,WMS%7C%7CSGI_2010%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_2010%7C%7C1.3.0&X=90392.14&Y=629484.66&zoom=6&layers_visibility=false,false,false'
                    classes='ui small black header'
                  />
                </div>
                <div className='three wide centered column'>
                  <Glacier
                    n='Unteraar'
                    k='BE'
                    l='-9.29'
                    p='-27.82'
                    url='https://map.geo.admin.ch/?topic=ech&lang=en&bgLayer=ch.swisstopo.pixelkarte-farbe&layers=WMS%7C%7CSGI_1850%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1850%7C%7C1.3.0,WMS%7C%7CSGI_1973%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1973%7C%7C1.3.0,WMS%7C%7CSGI_2010%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_2010%7C%7C1.3.0&X=157368.23&Y=659702.00&zoom=6&layers_visibility=false,false,false'
                    classes='ui small black header'
                  />
                </div>
                <div className='three wide centered column'>
                  <Glacier
                    n='Oberaletsch'
                    k='VS'
                    l='-8.41'
                    p='-31.661'
                    url='https://map.geo.admin.ch/?topic=ech&lang=en&bgLayer=ch.swisstopo.pixelkarte-farbe&layers=WMS%7C%7CSGI_1850%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1850%7C%7C1.3.0,WMS%7C%7CSGI_1973%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1973%7C%7C1.3.0,WMS%7C%7CSGI_2010%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_2010%7C%7C1.3.0&X=141636.69&Y=640768.94&zoom=6&layers_visibility=false,false,false'
                    classes='ui small black header'
                  />
                </div>
                <div className='three wide centered column'>
                  <Glacier
                    n='Otemma'
                    k='VS'
                    l='-7.40'
                    p='-36.15'
                    url='https://map.geo.admin.ch/?topic=ech&lang=en&bgLayer=ch.swisstopo.pixelkarte-farbe&layers=WMS%7C%7CSGI_1850%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1850%7C%7C1.3.0,WMS%7C%7CSGI_1973%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1973%7C%7C1.3.0,WMS%7C%7CSGI_2010%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_2010%7C%7C1.3.0&X=89862.59&Y=601639.19&zoom=6&layers_visibility=false,false,false'
                    classes='ui small black header'
                  />
                </div>
                <div className='three wide centered column'>
                  <Glacier
                    n='Mont Collon'
                    k='VS'
                    l='-6.89'
                    p='-42.042'
                    url='https://map.geo.admin.ch/?topic=ech&lang=en&bgLayer=ch.swisstopo.pixelkarte-farbe&layers=WMS%7C%7CSGI_1850%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1850%7C%7C1.3.0,WMS%7C%7CSGI_1973%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1973%7C%7C1.3.0,WMS%7C%7CSGI_2010%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_2010%7C%7C1.3.0&X=91476.80&Y=603853.19&zoom=6&layers_visibility=false,false,false'
                    classes='ui small black header'
                  />
                </div>
                <div className='three wide centered column'>
                  <Glacier
                    n='Fiescher'
                    k='VS'
                    l='-6.76'
                    p='-18.090'
                    url='https://map.geo.admin.ch/?topic=ech&lang=en&bgLayer=ch.swisstopo.pixelkarte-farbe&layers=WMS%7C%7CSGI_1850%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1850%7C%7C1.3.0,WMS%7C%7CSGI_1973%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1973%7C%7C1.3.0,WMS%7C%7CSGI_2010%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_2010%7C%7C1.3.0&X=149467.66&Y=652767.38&zoom=5&layers_visibility=false,false,false'
                    classes='ui small black header'
                  />
                </div>
                <div className='three wide centered column'>
                  <Glacier
                    n='Unterer Grindelwald'
                    k='BE'
                    l='-6.54'
                    p='-28.13'
                    url='https://map.geo.admin.ch/?topic=ech&lang=en&bgLayer=ch.swisstopo.pixelkarte-farbe&layers=WMS%7C%7CSGI_1850%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1850%7C%7C1.3.0,WMS%7C%7CSGI_1973%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1973%7C%7C1.3.0,WMS%7C%7CSGI_2010%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_2010%7C%7C1.3.0&X=160055.58&Y=647534.44&zoom=7&layers_visibility=false,false,false'
                    classes='ui small black header'
                  />
                </div>
                <div className='three wide centered column'>
                  <Glacier
                    n='Zmutt'
                    k='VS'
                    l='-6.36'
                    p='-31.66'
                    url='https://map.geo.admin.ch/?topic=ech&lang=en&bgLayer=ch.swisstopo.pixelkarte-farbe&layers=WMS%7C%7CSGI_1850%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1850%7C%7C1.3.0,WMS%7C%7CSGI_1973%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1973%7C%7C1.3.0,WMS%7C%7CSGI_2010%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_2010%7C%7C1.3.0&X=93989.05&Y=615726.81&zoom=7&layers_visibility=false,false,false'
                    classes='ui small black header'
                  />
                </div>
                <div className='three wide centered column'>
                  <Glacier
                    n='Findelen'
                    k='VS'
                    l='-5.62'
                    p='-25.70'
                    url='https://map.geo.admin.ch/?topic=ech&lang=en&bgLayer=ch.swisstopo.pixelkarte-farbe&layers=WMS%7C%7CSGI_1850%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1850%7C%7C1.3.0,WMS%7C%7CSGI_1973%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1973%7C%7C1.3.0,WMS%7C%7CSGI_2010%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_2010%7C%7C1.3.0&X=95029.98&Y=630896.97&zoom=7&layers_visibility=false,false,false'
                    classes='ui small black header'
                  />
                </div>
                <div className='three wide centered column'>
                  <Glacier
                    n='Morteratsch'
                    k='GR'
                    l='-5.38'
                    p='-26.58'
                    url='https://map.geo.admin.ch/?topic=ech&lang=en&bgLayer=ch.swisstopo.pixelkarte-farbe&layers=WMS%7C%7CSGI_1850%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1850%7C%7C1.3.0,WMS%7C%7CSGI_1973%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1973%7C%7C1.3.0,WMS%7C%7CSGI_2010%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_2010%7C%7C1.3.0&X=142239.31&Y=791442.31&zoom=6&layers_visibility=false,false,false'
                    classes='ui small black header'
                  />
                </div>
                <div className='three wide centered column'>
                  <Glacier
                    n='Plaine Morte'
                    k='BE'
                    l='-4.79'
                    p='-39.13'
                    url='https://map.geo.admin.ch/?topic=ech&lang=en&bgLayer=ch.swisstopo.pixelkarte-farbe&layers=WMS%7C%7CSGI_1850%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1850%7C%7C1.3.0,WMS%7C%7CSGI_1973%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1973%7C%7C1.3.0,WMS%7C%7CSGI_2010%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_2010%7C%7C1.3.0&X=137153.45&Y=605375.56&zoom=7&layers_visibility=false,false,false'
                    classes='ui small black header'
                  />
                </div>
                <div className='three wide centered column'>
                  <Glacier
                    n='Rhone'
                    k='VS'
                    l='-4.69'
                    p='-23.45'
                    url='https://map.geo.admin.ch/?topic=ech&lang=en&bgLayer=ch.swisstopo.pixelkarte-farbe&layers=WMS%7C%7CSGI_1850%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1850%7C%7C1.3.0,WMS%7C%7CSGI_1973%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1973%7C%7C1.3.0,WMS%7C%7CSGI_2010%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_2010%7C%7C1.3.0&X=163279.80&Y=673737.75&zoom=5&layers_visibility=false,false,false'
                    classes='ui small black header'
                  />
                </div>
                <div className='three wide centered column'>
                  <Glacier
                    n='Trift'
                    k='BE'
                    l='-4.65'
                    p='-23.77'
                    url='https://map.geo.admin.ch/?topic=ech&lang=en&bgLayer=ch.swisstopo.pixelkarte-farbe&layers=WMS%7C%7CSGI_1850%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1850%7C%7C1.3.0,WMS%7C%7CSGI_1973%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1973%7C%7C1.3.0,WMS%7C%7CSGI_2010%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_2010%7C%7C1.3.0&X=148601.02&Y=651862.44&zoom=8&layers_visibility=false,false,false'
                    classes='ui small black header'
                  />
                </div>
                <div className='three wide centered column'>
                  <Glacier
                    n='Forno'
                    k='GR'
                    l='-4.56'
                    p='-42.18'
                    url='ttps://map.geo.admin.ch/?topic=ech&lang=en&bgLayer=ch.swisstopo.pixelkarte-farbe&layers=WMS%7C%7CSGI_1850%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1850%7C%7C1.3.0,WMS%7C%7CSGI_1973%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1973%7C%7C1.3.0,WMS%7C%7CSGI_2010%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_2010%7C%7C1.3.0&X=132173.44&Y=774545.00&zoom=6&layers_visibility=false,false,false'
                    classes='ui small black header'
                  />
                </div>
                <div className='three wide centered column'>
                  <Glacier
                    n='Zinal'
                    k='VS'
                    l='-4.55'
                    p='-25.39'
                    url='https://map.geo.admin.ch/?topic=ech&lang=en&bgLayer=ch.swisstopo.pixelkarte-farbe&layers=WMS%7C%7CSGI_1850%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1850%7C%7C1.3.0,WMS%7C%7CSGI_1973%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1973%7C%7C1.3.0,WMS%7C%7CSGI_2010%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_2010%7C%7C1.3.0&X=101801.37&Y=615055.13&zoom=7&layers_visibility=false,false,false'
                    classes='ui small black header'
                  />
                </div>
                <div className='three wide centered column'>
                  <Glacier
                    n='Stein'
                    k='BE'
                    l='-4.12'
                    p='-36.098'
                    url='https://map.geo.admin.ch/?topic=ech&lang=en&bgLayer=ch.swisstopo.pixelkarte-farbe&layers=WMS%7C%7CSGI_1850%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1850%7C%7C1.3.0,WMS%7C%7CSGI_1973%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1973%7C%7C1.3.0,WMS%7C%7CSGI_2010%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_2010%7C%7C1.3.0&X=173057.77&Y=675827.13&zoom=7&layers_visibility=false,false,false'
                    classes='ui small black header'
                  />
                </div>
                <div className='three wide centered column'>
                  <Glacier
                    n='Lang'
                    k='VS'
                    l='-3.98'
                    p='-31.818'
                    url='https://map.geo.admin.ch/?topic=ech&lang=en&bgLayer=ch.swisstopo.pixelkarte-farbe&layers=WMS%7C%7CSGI_1850%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1850%7C%7C1.3.0,WMS%7C%7CSGI_1973%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1973%7C%7C1.3.0,WMS%7C%7CSGI_2010%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_2010%7C%7C1.3.0&X=145283.41&Y=637615.88&zoom=8&layers_visibility=false,false,false'
                    classes='ui small black header'
                  />
                </div>
                <div className='three wide centered column'>
                  <Glacier
                    n='Brenay'
                    k='VS'
                    l='-3.91'
                    p='-35.48'
                    url='https://map.geo.admin.ch/?topic=ech&lang=en&bgLayer=ch.swisstopo.pixelkarte-farbe&layers=WMS%7C%7CSGI_1850%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1850%7C%7C1.3.0,WMS%7C%7CSGI_1973%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1973%7C%7C1.3.0,WMS%7C%7CSGI_2010%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_2010%7C%7C1.3.0&X=90680.48&Y=598871.88&zoom=7&layers_visibility=false,false,false'
                    classes='ui small black header'
                  />
                </div>
                <div className='three wide centered column'>
                  <Glacier
                    n='Kanderfirn'
                    k='BE'
                    l='-3.81'
                    p='-23.74'
                    url='https://map.geo.admin.ch/?topic=ech&lang=en&bgLayer=ch.swisstopo.pixelkarte-farbe&layers=WMS%7C%7CSGI_1850%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1850%7C%7C1.3.0,WMS%7C%7CSGI_1973%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1973%7C%7C1.3.0,WMS%7C%7CSGI_2010%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_2010%7C%7C1.3.0&X=147586.34&Y=627355.38&zoom=7&layers_visibility=false,false,false'
                    classes='ui small black header'
                  />
                </div>
                <div className='three wide centered column'>
                  <Glacier
                    n='Ferpècle'
                    k='VS'
                    l='-3.78'
                    p='-26.484'
                    url='https://map.geo.admin.ch/?topic=ech&lang=en&bgLayer=ch.swisstopo.pixelkarte-farbe&layers=WMS%7C%7CSGI_1850%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1850%7C%7C1.3.0,WMS%7C%7CSGI_1973%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1973%7C%7C1.3.0,WMS%7C%7CSGI_2010%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_2010%7C%7C1.3.0&X=96185.23&Y=610439.50&zoom=6&layers_visibility=false,false,false'
                    classes='ui small black header'
                  />
                </div>
                <div className='three wide centered column'>
                  <Glacier
                    n='Roseg'
                    k='GR'
                    l='-3.44'
                    p='-33.56'
                    url='https://map.geo.admin.ch/?topic=ech&lang=en&bgLayer=ch.swisstopo.pixelkarte-farbe&layers=WMS%7C%7CSGI_1850%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1850%7C%7C1.3.0,WMS%7C%7CSGI_1973%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1973%7C%7C1.3.0,WMS%7C%7CSGI_2010%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_2010%7C%7C1.3.0&X=139332.30&Y=785001.63&zoom=7&layers_visibility=false,false,false'
                    classes='ui small black header'
                  />
                </div>
                <div className='three wide centered column'>
                  <Glacier
                    n='Corbassière'
                    k='VS'
                    l='-3.37'
                    p='-16.275'
                    url='https://map.geo.admin.ch/?topic=ech&lang=en&bgLayer=ch.swisstopo.pixelkarte-farbe&layers=WMS%7C%7CSGI_1850%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1850%7C%7C1.3.0,WMS%7C%7CSGI_1973%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1973%7C%7C1.3.0,WMS%7C%7CSGI_2010%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_2010%7C%7C1.3.0&X=92032.63&Y=589613.88&zoom=6&layers_visibility=false,false,false'
                    classes='ui small black header'
                  />
                </div>
                <div className='three wide centered column'>
                  <Glacier
                    n='Tschierva'
                    k='GR'
                    l='-3.26'
                    p='-39.00'
                    url='https://map.geo.admin.ch/?topic=ech&lang=en&bgLayer=ch.swisstopo.pixelkarte-farbe&layers=WMS%7C%7CSGI_1850%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1850%7C%7C1.3.0,WMS%7C%7CSGI_1973%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1973%7C%7C1.3.0,WMS%7C%7CSGI_2010%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_2010%7C%7C1.3.0&X=140825.69&Y=788126.56&zoom=7&layers_visibility=false,false,false'
                    classes='ui small black header'
                  />
                </div>
                <div className='three wide centered column'>
                  <Glacier
                    n='Saleina'
                    k='VS'
                    l='-2.92'
                    p='-30.129'
                    url='https://map.geo.admin.ch/?topic=ech&lang=en&bgLayer=ch.swisstopo.pixelkarte-farbe&layers=WMS%7C%7CSGI_1850%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1850%7C%7C1.3.0,WMS%7C%7CSGI_1973%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1973%7C%7C1.3.0,WMS%7C%7CSGI_2010%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_2010%7C%7C1.3.0&X=91268.85&Y=569459.31&zoom=7&layers_visibility=false,false,false'
                    classes='ui small black header'
                  />
                </div>
                <div className='three wide centered column'>
                  <Glacier
                    n='Palü'
                    k='GR'
                    l='-2.83'
                    p='-34.94'
                    url='https://map.geo.admin.ch/?topic=ech&lang=en&bgLayer=ch.swisstopo.pixelkarte-farbe&layers=WMS%7C%7CSGI_1850%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1850%7C%7C1.3.0,WMS%7C%7CSGI_1973%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1973%7C%7C1.3.0,WMS%7C%7CSGI_2010%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_2010%7C%7C1.3.0&X=138382.75&Y=795278.31&zoom=7&layers_visibility=false,false,false'
                    classes='ui small black header'
                  />
                </div>
                <div className='three wide centered column'>
                  <Glacier
                    n='Mont Durand'
                    k='VS'
                    l='-2.82'
                    p='-31.76'
                    url='https://map.geo.admin.ch/?topic=ech&lang=en&bgLayer=ch.swisstopo.pixelkarte-farbe&layers=WMS%7C%7CSGI_1850%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1850%7C%7C1.3.0,WMS%7C%7CSGI_1973%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1973%7C%7C1.3.0,WMS%7C%7CSGI_2010%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_2010%7C%7C1.3.0&X=85593.58&Y=591160.19&zoom=7&layers_visibility=false,false,false'
                    classes='ui small black header'
                  />
                </div>
                <div className='three wide centered column'>
                  <Glacier
                    n='Mont Miné'
                    k='VS'
                    l='-2.77'
                    p='-21.86'
                    url='https://map.geo.admin.ch/?topic=ech&lang=en&bgLayer=ch.swisstopo.pixelkarte-farbe&layers=WMS%7C%7CSGI_1850%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1850%7C%7C1.3.0,WMS%7C%7CSGI_1973%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1973%7C%7C1.3.0,WMS%7C%7CSGI_2010%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_2010%7C%7C1.3.0&X=95727.59&Y=608275.75&zoom=6&layers_visibility=false,false,false'
                    classes='ui small black header'
                  />
                </div>
                <div className='three wide centered column'>
                  <Glacier
                    n='Hüfifirn'
                    k='UR'
                    l='-2.48'
                    p='-16.29'
                    url='https://map.geo.admin.ch/?topic=ech&lang=en&bgLayer=ch.swisstopo.pixelkarte-farbe&layers=WMS%7C%7CSGI_1850%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1850%7C%7C1.3.0,WMS%7C%7CSGI_1973%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1973%7C%7C1.3.0,WMS%7C%7CSGI_2010%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_2010%7C%7C1.3.0&X=186108.03&Y=708461.44&zoom=6&layers_visibility=false,false,false'
                    classes='ui small black header'
                  />
                </div>
                <div className='three wide centered column'>
                  <Glacier
                    n='Schwarzberg'
                    k='VS'
                    l='-2.26'
                    p='-28.368'
                    url='https://map.geo.admin.ch/?topic=ech&lang=en&bgLayer=ch.swisstopo.pixelkarte-farbe&layers=WMS%7C%7CSGI_1850%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1850%7C%7C1.3.0,WMS%7C%7CSGI_1973%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1973%7C%7C1.3.0,WMS%7C%7CSGI_2010%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_2010%7C%7C1.3.0&X=95779.33&Y=638230.75&zoom=7&layers_visibility=false,false,false'
                    classes='ui small black header'
                  />
                </div>
                <div className='three wide centered column'>
                  <Glacier
                    n='Tschingelfirn'
                    k='BE'
                    l='-2.25'
                    p='-30.08'
                    url='https://map.geo.admin.ch/?topic=ech&lang=en&bgLayer=ch.swisstopo.pixelkarte-farbe&layers=WMS%7C%7CSGI_1850%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1850%7C%7C1.3.0,WMS%7C%7CSGI_1973%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1973%7C%7C1.3.0,WMS%7C%7CSGI_2010%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_2010%7C%7C1.3.0&X=180629.05&Y=705867.50&zoom=8&layers_visibility=false,false,false'
                    classes='ui small black header'
                  />
                </div>
                <div className='three wide centered column'>
                  <Glacier
                    n='Moming'
                    k='VS'
                    l='-2.23'
                    p='-29.73'
                    url='https://map.geo.admin.ch/?topic=ech&lang=en&bgLayer=ch.swisstopo.pixelkarte-farbe&layers=WMS%7C%7CSGI_1850%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1850%7C%7C1.3.0,WMS%7C%7CSGI_1973%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1973%7C%7C1.3.0,WMS%7C%7CSGI_2010%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_2010%7C%7C1.3.0&X=103728.39&Y=618275.25&zoom=7&layers_visibility=false,false,false'
                    classes='ui small black header'
                  />
                </div>
                <div className='three wide centered column'>
                  <Glacier
                    n='Allalin'
                    k='VS'
                    l='-1.97'
                    p='-17.70'
                    url='https://map.geo.admin.ch/?topic=ech&lang=en&bgLayer=ch.swisstopo.pixelkarte-farbe&layers=WMS%7C%7CSGI_1850%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1850%7C%7C1.3.0,WMS%7C%7CSGI_1973%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1973%7C%7C1.3.0,WMS%7C%7CSGI_2010%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_2010%7C%7C1.3.0&X=98770.71&Y=636995.13&zoom=6&layers_visibility=false,false,false'
                    classes='ui small black header'
                  />
                </div>
                <div className='three wide centered column'>
                  <Glacier
                    n='Oberer Grindelwald'
                    k='BE'
                    l='-1.71'
                    url='https://map.geo.admin.ch/?topic=ech&lang=en&bgLayer=ch.swisstopo.pixelkarte-farbe&layers=WMS%7C%7CSGI_1850%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1850%7C%7C1.3.0,WMS%7C%7CSGI_1973%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1973%7C%7C1.3.0,WMS%7C%7CSGI_2010%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_2010%7C%7C1.3.0&X=163036.03&Y=650866.75&zoom=7&layers_visibility=false,false,false'
                    p='-16.90'
                    classes='ui small black header'
                  />
                </div>
                <div className='three wide centered column'>
                  <Glacier
                    n='Ried'
                    k='VS'
                    l='-1.66'
                    p='-18.49'
                    url='https://map.geo.admin.ch/?topic=ech&lang=en&bgLayer=ch.swisstopo.pixelkarte-farbe&layers=WMS%7C%7CSGI_1850%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1850%7C%7C1.3.0,WMS%7C%7CSGI_1973%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1973%7C%7C1.3.0,WMS%7C%7CSGI_2010%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_2010%7C%7C1.3.0&X=109573.70&Y=631829.94&zoom=6&layers_visibility=false,false,false'
                    classes='ui small black header'
                  />
                </div>
                <div className='three wide centered column'>
                  <Glacier
                    n='Rosenlaui'
                    k='BE'
                    l='-1.56'
                    p='-22.38'
                    url='https://map.geo.admin.ch/?topic=ech&lang=en&bgLayer=ch.swisstopo.pixelkarte-farbe&layers=WMS%7C%7CSGI_1850%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1850%7C%7C1.3.0,WMS%7C%7CSGI_1973%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1973%7C%7C1.3.0,WMS%7C%7CSGI_2010%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_2010%7C%7C1.3.0&X=167120.63&Y=654793.69&zoom=7&layers_visibility=false,false,false'
                    classes='ui small black header'
                  />
                </div>
                <div className='three wide centered column'>
                  <Glacier
                    n='Trient'
                    k='VS'
                    l='-1.40'
                    p='-19.36'
                    url='https://map.geo.admin.ch/?topic=ech&lang=en&bgLayer=ch.swisstopo.pixelkarte-farbe&layers=WMS%7C%7CSGI_1850%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1850%7C%7C1.3.0,WMS%7C%7CSGI_1973%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1973%7C%7C1.3.0,WMS%7C%7CSGI_2010%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_2010%7C%7C1.3.0&X=95604.60&Y=568027.78&zoom=7&layers_visibility=false,false,false'
                    classes='ui small black header'
                  />
                </div>
                <div className='three wide centered column'>
                  <Glacier
                    n='Giétro'
                    k='VS'
                    l='-1.27'
                    p='-19.72'
                    url='https://map.geo.admin.ch/?topic=ech&lang=en&bgLayer=ch.swisstopo.pixelkarte-farbe&layers=WMS%7C%7CSGI_1850%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1850%7C%7C1.3.0,WMS%7C%7CSGI_1973%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_1973%7C%7C1.3.0,WMS%7C%7CSGI_2010%7C%7Chttp:%2F%2Fwww.glamos.ch%2Fqgis%2Fsgi%7C%7CSGI_2010%7C%7C1.3.0&X=93892.82&Y=595912.25&zoom=7&layers_visibility=false,false,false'
                    classes='ui small black header'
                  />
                </div>
              </div>
            </div>
            <Waypoint
              scrollableAncestor={window}
              onEnter={event => {
                if (event.previousPosition === 'below') {
                  window.dataLayer = window.dataLayer || []
                  window.dataLayer.push({
                    event: '50ContentRead'
                  })
                }
              }}
            />
          </div>
          <div className='ui ta text container'>
            <FeedbackMessage />
          </div>
        </div>

        <div className='ui vertical segment' style={{ paddingTop: '1em' }}>
          <div className='ui ta text container'>
            <h4>Gibt es Gletscher, die bereits komplett verschwunden sind?</h4>
            <p>
              1973 zählte die Schweiz 2150 Gletscher, heute sind es noch rund
              1400. Damit sind nur schon in diesem Zeitraum 750 Gletscher
              verschwunden. Die meisten von ihnen waren klein und besassen nie
              einen Namen. Besonders bedauerlich: Alle Gletscher im Schweizer
              Nationalpark sind bereits komplett geschmolzen. 1973 gab es dort
              noch rund ein Dutzend – einzig der Vadret da Nuna hatte einen
              Namen.
            </p>
            <h3>Drei Beispiele von bereits geschmolzenen Gletschern</h3>
            <div className='ui grid'>
              <div className='three column row'>
                <div className='five wide centered column'>
                  <VanishedGlacier n='Mucia' k='GR' l='1850' p='2.25' />
                </div>
                <div className='five wide centered column'>
                  <VanishedGlacier n='Lucendro' k='TI' l='1850' p='0.44' />
                </div>
                <div className='five wide centered column'>
                  <VanishedGlacier n='Ofental' k='VS' l='1850' p='0.98' />
                </div>
              </div>
            </div>

            <h4>
              Hat sich der Gletscherschwund in den vergangenen Jahren
              beschleunigt?
            </h4>
            <p>
              Seit Beginn der Aufzeichnungen war die Gletscherschmelze noch nie
              so stark wie in den letzten Jahren. Die weltweit längste Messreihe
              der Gletscherschmelze (102 Jahre) auf dem Glarner Claridenfirn
              zeigt: Von den acht extremsten Schmelz-Jahren fanden sechs nach
              2008 statt.{' '}
              <strong>
                Letztes Jahr verloren die Gletscher fast 1 km&sup3; an
                Eisvolumen oder rund 900 Milliarden Liter Wasser.
              </strong>
            </p>
            <img
              src={require('./img/volumen.svg')}
              alt=''
              className='ui fluid image'
            />
            <p>
              Die Dicke des Gletschereises nimmt im Moment durchschnittlich um
              etwa einen Meter pro Jahr ab, das Eis büsst rund ein Prozent
              seiner Fläche ein. Global schmolzen die Gletscher von 2001 bis
              2010 zwei- bis dreimal so stark wie im Mittel des gesamten 20.
              Jahrhunderts.
            </p>

            <h4>Wie schlimm ist bisher das Jahr 2017?</h4>
            <p>
              Die Hitzewellen im Juni und Anfang Juli waren sehr ungünstig für
              die Gletscher. Die Schneedecke aus dem Winter liegt nur noch in
              den oberen Bereichen der Gletscher. Abgerechnet wird aber am Ende
              des Sommers. Wenn es weiterhin so warm bleibt, kann es ein
              Schmelz-Rekordjahr geben. Aber klar ist sowieso: 2017 wird kein
              gutes Jahr mehr.
            </p>

            <h4>
              In welcher Schweizer Region lässt sich der Gletscherschwund am
              dramatischsten beobachten?
            </h4>
            <p>
              Im Wallis sind die Gletscher am grössten, sie reichen am weitesten
              ins Tal und sind uns damit auch am nächsten. Der Schwund ist also
              dort am besten sichtbar, beispielsweise beim Rhone- oder
              Aletschgletscher. Das gilt aber auch für den Morteratschgletscher
              in Graubünden oder in Grindelwald (BE), wo der Gletscher vor 160
              Jahren noch bis ins Dorf floss. Der Triftgletscher (BE) hat
              zwischen 2000 und 2015 zwei Kilometer an Länge verloren. In Bezug
              auf ihre Gesamtfläche schrumpfen grosse Gletscher etwa zwei- bis
              dreimal weniger schnell als die kleinsten. Diese haben seit 1850
              fast 90 Prozent ihrer Fläche verloren, während die grossen
              Gletscher «nur» zwischen 15 und 40 Prozent eingebüsst haben.
            </p>
          </div>

          <div className='ui vertical segment'>
            <div className='ui container slider'>
              <AutofitIFrame src='https://interaktiv.tagesanzeiger.ch/2015/slider/?before=https://interaktiv.tagesanzeiger.ch/2017/gletscherschwund/fotos/rhonegletscher_2010.jpg&after=https://interaktiv.tagesanzeiger.ch/2017/gletscherschwund/fotos/rhonegletscher_2017.jpg' />
              <div className='source'>
                Der Vergleich vom 23.9.2010 und 23.6.2017 zeigt, in welch hohem
                Tempo der Rhonegletscher wegschmilzt.{' '}
                <i>
                  Foto: Simon Oberli,{' '}
                  <a href='http://gletschervergleiche.ch' target='_blank'>
                    GletscherVergleiche.ch
                  </a>
                </i>
              </div>
            </div>
          </div>

          <div className='ui text container' style={{ position: 'relative' }}>
            <h4>Seit wann schmelzen die Gletscher?</h4>
            <p>
              Gletscher schmelzen im unteren Bereich im Sommer immer. Seit etwa
              1850 verlieren sie aber an Masse. Damals war die sogenannte Kleine
              Eiszeit auf dem Höchststand. Die Gründe sind eindeutig: deutlich
              steigende Temperaturen. Zusätzlich beeinflussen auch Änderungen
              des Schneefalls sowie der Sonneneinstrahlung die Geschwindigkeit
              der Schmelze. Hauptsächlich kann allerdings die Temperatur während
              des Sommers verantwortlich gemacht werden. Gletscher zählen zu den
              besten natürlichen Klimaindikatoren und sind ein Schlüsselelement
              im Monitoring des Klimawandels.
            </p>

            <h4>Ist der Mensch schuld?</h4>
            <p>
              Schon seit Jahrmillionen gehen die Gletscher zurück und stossen
              wieder vor, auch ganz ohne Mensch. Vor 20’000 Jahren war die
              Schweiz fast komplett von Eis bedeckt. Was allerdings in den
              letzten 150 Jahren aussergewöhnlich ist, ist die unglaubliche
              Geschwindigkeit der Erwärmung der Atmosphäre und damit des
              Gletscherrückgangs. Der Anstieg der Temperatur liegt weit jenseits
              dem in der Erdgeschichte Bekannten. Und: Der Temperaturanstieg
              korreliert mit den Emissionen von CO2 in die Atmosphäre, welche
              mit der Verbrennung von fossilen Energieträgern stark zugenommen
              haben. Der Mensch ist verantwortlich für den starken
              Gletscherrückgang.
            </p>

            <h4>Lässt sich der Gletscherschwund noch stoppen?</h4>
            <p>
              Bis 2100 wird fast die gesamte Eisfläche in der Schweiz
              verschwunden sein. Den Aletsch-Gletscher wird es zwar noch geben,
              allerdings nur noch aufgeteilt in kleinere Teile in hohen Lagen.
              Leider können auch mit den günstigsten Szenarien bei starker
              CO2-Reduktion die allermeisten Gletscher in der Schweiz nicht mehr
              gerettet werden: Es dürften so oder so 4 von 5 verschwinden, auch
              wenn schon morgen der Ausstoss von Treibhausgasen gestoppt werden
              könnte. Im Falle eines Anstiegs der Sommertemperatur in der
              Schweiz um 5 Grad würden die Alpen Ende des 21. Jahrhunderts
              praktisch eisfrei werden. Schweizer Klimaforscher gehen davon aus,
              dass es in der Schweiz gegenüber der vorindustriellen Zeit bis
              Ende des Jahrhunderts im Jahresdurchschnitt 3 bis 5 Grad heisser
              sein wird, wenn der Anstieg der Treibhausgase nicht gebremst wird.
              «Natürlich hoffe ich, dass wir es schaffen, die Emissionen so zu
              reduzieren, dass wir wenigstens 20% der Gletscher bewahren
              können», so Glaziologe Matthias Huss.
            </p>

            <h4>Was bringen Massnahmen wie Gletscherabdeckungen?</h4>
            <p>
              Am Titlis oder beim Rhonegletscher werden die Gletscher teils mit
              Vlies-Tüchern abgedeckt, um die Sonneneinstrahlung abzudämpfen. Im
              Engadin denkt man darüber nach, die Gletscher künstlich zu
              beschneien. Solche Massnahmen sind zwar lokal effizient und
              sinnvoll. Man kann damit den Rückgang an einer kleinen Stelle
              aufhalten, allerdings niemals einen ganzen Gletscher retten. Die
              Kosten dafür übersteigen bei weitem den Nutzen.
            </p>

            <h4>Was, wenn alle Gletscher weg sind?</h4>
            <p>
              Lebensbedrohlich ist es nicht. Trotzdem sind die Folgen drastisch.
            </p>
            <p>
              <strong>Tourismus: </strong>Gletscher sind ein Touristenmagnet und
              ein wichtiges Identifikationsmerkmal der Schweiz. Wenn es keine
              Gletscher gibt, muss sich das Land neu präsentieren. Unsere Alpen
              bleiben schön, auch mit weniger Eis. Sie werden einfach anders.
            </p>
            <p>
              <strong>Wasserhaushalt: </strong>Gletscher speichern Wasser im
              Winter und geben es im Sommer frei. Zudem speichern sie Wasser in
              kühlen, nassen Jahren und geben es in trockenen, heissen Sommern
              wieder frei. Sie haben damit eine zentrale Rolle in der
              Regulierung des Abflusses. Sind die Gletscher weg, fällt diese
              Funktion weg. Das wird einen Einfluss haben auf die
              Wasserverfügbarkeit in Alpenregionen, aber auch darüber hinaus, da
              die grossen Flüsse Europas in den Alpen entspringen und im Sommer
              massgeblich von den Gletschern gespeist werden.
            </p>
            <p>
              <strong>Wasserkraft: </strong>Mit der Energiestrategie 2050 will
              die Schweiz auf Wasserkraft setzen. Fast alle Stauseen beziehen
              ihr Wasser von Gletschern. Auch in Zukunft werden sie gefüllt
              werden, doch das Wasser kommt zu anderen Zeitpunkten im Jahr.
            </p>
            <p>
              <strong>Naturgefahren: </strong>Wenn sich Gletscher zurückziehen,
              verändert sich die Landschaft. Das kann zu Problemen mit der
              Stabilität von Hängen führen. Neue Seen bilden sich. Diese können
              ausbrechen, besonders, wenn sie vom Eis gestaut werden.
            </p>
            <p>
              <strong>Anstieg des Meeresspiegels: </strong>Man geht davon aus,
              dass der globale Meeresspiegel bis 2100 um 0,3 bis 1 m steigen
              könnte. Die Gletscherschmelze liefert einen beträchtlichen Beitrag
              zu diesem Anstieg. Ein steigender Meeresspiegel betrifft uns in
              der Schweiz nicht direkt, aber es wird Millionen von
              Klimaflüchtlingen geben.
            </p>

            <h4>Stimmt es, dass in Norwegen die Gletscher wachsen?</h4>
            <p>
              Das ist nicht mehr aktuell. Die kurze Wachstumsphase endete vor
              ein paar Jahren. Gletscher in Norwegen sind sehr stark von der
              Feuchtigkeit beeinflusst, die vom Ozean kommt. Die stieg aufgrund
              von veränderter Zirkulation der Luftmassen kurzfristig an. Das ist
              nun aber vorbei, und die Gletscher folgen wieder ihrem
              langfristigen Trend zu einem Verlust. Der Gletscherschwund ist ein
              globales Problem. Weltweit gibt es rund 200’000 Gletscher. 730’000
              Quadratkilometer beträgt die totale Gletscherfläche der Erde – so
              gross wie Deutschland, Polen und die Schweiz zusammen. Dazu kommen
              die beiden Eisschilde in Grönland und der Antarktis, die zusammen
              gleich gross sind wie Russland. 70 Prozent des Süsswassers sind in
              Eis gespeichert.
            </p>
            <Waypoint
              scrollableAncestor={window}
              onEnter={event => {
                if (event.previousPosition === 'below') {
                  window.dataLayer = window.dataLayer || []
                  window.dataLayer.push({
                    event: 'contentBottom'
                  })
                }
              }}
            />
          </div>
        </div>

        <div className='ui vertical segment' style={{ background: '#f5f5f5' }}>
          <div className='ui text container' style={{ position: 'relative' }}>
            <h4>Expertise</h4>
            <p>
              Matthias Huss, Glaziologe, ETH Zürich/Universität Freiburg, Leiter
              Schweizer Gletschermessnetz.
            </p>
            <h4>Referenzen, Methodik, Quellen</h4>
            <p>
              Inventare fassen die Flächen und Umrisse aller Gletscher zu einem
              bestimmten Zeitpunkt zusammen. In der Schweiz sind komplette
              Gletscherinventare für 1850, 1973 und 2010 vorhanden. Für die
              Inventare von 1973 und 2010 wurden detaillierte Luftbilder
              verwendet, die im August und September aufgenommen werden, wenn
              kaum mehr Schnee liegt. Das Inventar von 1850 basiert auf heute
              noch sichtbaren Moränen im Gletschervorfeld. Zusätzlich wurden
              Kartierungen der Schweizer Gletscher für 1999 und 2003 mittels
              weniger detaillierter Satellitenbilder erstellt. Für die aktuellen
              Daten im Beitrag «So schmolzen die Schweizer Gletscher in 160
              Jahren weg» wurden Werte von 2010 auf 2016 extrapoliert. Ein neues
              komplettes Gletscherinventar wird vom Schweizer Gletschermessnetz
              (GLAMOS) in Zusammenarbeit mit Swisstopo im Jahr 2020 erstellt.​
            </p>
            <p>
              Wieso ist es aufschlussreicher, den Gletscherrückgang über das
              Eisvolumen zu dokumentieren? Die Veränderung des Gletschervolumens
              reagiert sofort, wenn sich das Klima ändert. Die Länge der
              Gletscherzunge hingegen hängt neben dem Klima auch von anderen
              Faktoren ab, beispielsweise, wie steil und dick der Gletscher an
              einer bestimmten Stelle ist. So können sich sehr grosse Rückzüge
              innerhalb eines Jahres abspielen, obwohl es gar nicht so warm war.
              Um jährliche Volumenänderung des Gletschers – die sogenannte
              Massenbilanz – zu messen, bohren die Forscher Stangen ins Eis. Im
              Frühling und Herbst messen sie dann, wie viel die Stangen aus dem
              Eis ragen. So können sie den Zuwachs oder die Schmelze berechnen.
            </p>
            <hr />
            <div className='sources'>
              <h4>
                Quellen der Inventare für die Berechnung der Gletscher-Eisfläche
                (1850, 1973, 2010)
              </h4>
              <p>
                Maisch, M., Wipf, A., Denneler, B., Battaglia, J., and Benz, C.,
                2000: Die Gletscher der Schweizer Alpen: Gletscherhochstand
                1850, Aktuelle Vergletscherung, Gletscherschwundszenarien.
                Schlussbericht NFP 31. Second edition. Zurich: vdf
                Hochschulverlag ETH Zurich, 373 p.
              </p>
              <p>
                Müller, F., Caflish, T., and Müller, G., 1976: Firn und Eis der
                Schweizer Alpen, Gletscherinventar. Zurich: vdf Hochschulverlag
                ETH Zurich, 224 p.
              </p>
              <p>
                Fischer, M., Huss, M., Barboux, C., and Hoelzle, M.: The new
                Swiss Glacier Inventory SGI2010: Relevance of using high-
                resolution source data in areas dominated by very small
                glaciers, Arct., Antarct. Alp. Res., 46, 935–947,
                doi:10.1657/1938-4246- 46.4.933, 2014.
              </p>
            </div>
          </div>
        </div>
      </main>
      <Footer
        articleId={ARTICLE_ID}
        additionalShares={ADDITIONAL_SHARES}
        hideUserFeedback
        hidePortfolioTeaser
        topHalfClassName='white'
        bottomHalfClassName='white'
        credits={[
          [
            'Text, digitales Konzept und Programmierung',
            'Marc Brupbacher und Mathias Lutz, Interaktiv-Team'
          ],
          [
            'Inspiration',
            '<a href="https://www.nytimes.com/interactive/2017/05/24/climate/mapping-50-years-of-ice-loss-in-glacier-national-park.html" target="_blank">Mapping 50 Years of Melting Ice in Glacier National Park</a>, «The New York Times»'
          ]
        ]}
      />
    </div>
  )
}
